import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  ImagePlus, Loader2, Sparkles, Upload, Trash2, Plus, X,
} from 'lucide-react';
import { toast } from 'sonner';

const LAYOUTS = [
  { id: 'landscape', label: 'Landscape (post)' },
  { id: 'portrait', label: 'Portrait (story)' },
  { id: 'square', label: 'Square' },
];

const empty = () => ({
  name: '', layout: 'landscape', headline: '', subheadline: '',
  text_color: '#ffffff', image_url: '', active: true,
});

// Convert a base64 PNG into a durable uploaded URL via /uploads/media.
async function persistBase64(api, b64) {
  const res = await fetch(`data:image/png;base64,${b64}`);
  const blob = await res.blob();
  const file = new File([blob], `banner-${Date.now()}.png`, { type: 'image/png' });
  const fd = new FormData();
  fd.append('file', file);
  const up = await api.post('/uploads/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return up.data.url;
}

const BannerForm = ({ api, onCreated, onCancel }) => {
  const [form, setForm] = useState(empty());
  const [prompt, setPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generateImg = async () => {
    if (prompt.trim().length < 3) { toast.error('Enter an image prompt'); return; }
    setGenerating(true);
    try {
      const r = await api.post('/generate/image', { prompt, model: 'nano-banana' });
      if (r.data?.image_base64) {
        const url = await persistBase64(api, r.data.image_base64);
        set('image_url', url);
        toast.success('Background generated');
      } else { toast.error('No image returned'); }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Generation failed'));
    } finally { setGenerating(false); }
  };

  const uploadImg = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await api.post('/uploads/media', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('image_url', up.data.url);
      toast.success('Image uploaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Give the banner a name'); return; }
    if (!form.image_url) { toast.error('Generate or upload a background image'); return; }
    setSaving(true);
    try {
      const r = await api.post('/affiliate/admin/banners', form);
      toast.success('Banner added');
      onCreated?.(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save banner'); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-4" data-testid="banner-form">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-zinc-800">New banner</p>
        <button onClick={onCancel} className="text-zinc-400 hover:text-zinc-600" data-testid="banner-form-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Name (internal)</Label>
          <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Black Friday 50% off" data-testid="banner-name" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Layout</Label>
          <Select value={form.layout} onValueChange={(v) => set('layout', v)}>
            <SelectTrigger data-testid="banner-layout"><SelectValue /></SelectTrigger>
            <SelectContent>{LAYOUTS.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Headline (use line breaks for 2-3 lines)</Label>
        <Textarea rows={2} value={form.headline} onChange={(e) => set('headline', e.target.value)} placeholder={'Black Friday\n50% off your first month'} data-testid="banner-headline" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Subheadline (optional)</Label>
          <Input value={form.subheadline} onChange={(e) => set('subheadline', e.target.value)} placeholder="Limited time only" data-testid="banner-subheadline" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Text color</Label>
          <div className="flex items-center gap-2 h-9">
            <input type="color" value={form.text_color} onChange={(e) => set('text_color', e.target.value)} className="w-9 h-9 rounded border border-zinc-200 cursor-pointer p-0" data-testid="banner-text-color" />
            <span className="text-xs text-zinc-500">{form.text_color}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-xs">Background image</Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="AI prompt: bold gradient with abstract shapes for a sale banner" data-testid="banner-ai-prompt" />
          <Button variant="outline" size="sm" onClick={generateImg} disabled={generating} className="shrink-0" data-testid="banner-generate-img">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5 text-indigo-600" />} Generate (AI)
          </Button>
          <label className="cursor-pointer shrink-0">
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg(e.target.files?.[0])} data-testid="banner-upload-input" />
            <span className="inline-flex items-center text-sm border border-zinc-200 rounded-md px-3 h-9 hover:bg-white bg-white">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />} Upload
            </span>
          </label>
        </div>
        {form.image_url && (
          <img src={form.image_url} alt="banner background" className="w-full max-h-40 object-cover rounded-md border border-zinc-200" data-testid="banner-image-preview" />
        )}
      </div>

      <div className="flex items-center justify-between pt-1">
        <label className="flex items-center gap-2 text-xs text-zinc-600">
          <Switch checked={form.active} onCheckedChange={(v) => set('active', v)} data-testid="banner-active" /> Active (visible to affiliates)
        </label>
        <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="banner-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />} Add banner
        </Button>
      </div>
    </div>
  );
};

export const AffiliateBannerAdmin = ({ api }) => {
  const [banners, setBanners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/affiliate/admin/banners');
      setBanners(r.data?.banners || []);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleActive = async (b) => {
    try {
      await api.put(`/affiliate/admin/banners/${b.id}`, { active: !b.active });
      setBanners((list) => list.map((x) => (x.id === b.id ? { ...x, active: !x.active } : x)));
    } catch (e) { toast.error('Could not update'); }
  };

  const remove = async (b) => {
    try {
      await api.delete(`/affiliate/admin/banners/${b.id}`);
      setBanners((list) => list.filter((x) => x.id !== b.id));
      toast.success('Banner deleted');
    } catch (e) { toast.error('Could not delete'); }
  };

  return (
    <Card className="border-indigo-200" data-testid="affiliate-banner-admin">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><ImagePlus className="w-4 h-4 text-indigo-600" /> Referral banners</CardTitle>
        {!showForm && (
          <Button size="sm" onClick={() => setShowForm(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="add-banner-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New banner
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-zinc-500">
          Add promo banners (sales, discounts, seasonal campaigns) for affiliates to share. Each affiliate's
          referral code &amp; link are automatically overlaid. The two default banners are always available.
        </p>

        {showForm && (
          <BannerForm api={api} onCancel={() => setShowForm(false)}
            onCreated={(b) => { setBanners((l) => [b, ...l]); setShowForm(false); }} />
        )}

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>
        ) : banners.length === 0 ? (
          <p className="text-sm text-zinc-400 text-center py-4" data-testid="no-banners">No custom banners yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-banner-list">
            {banners.map((b) => (
              <div key={b.id} className="rounded-lg border border-zinc-200 overflow-hidden" data-testid={`admin-banner-${b.id}`}>
                <img src={b.image_url} alt={b.name} className="w-full h-28 object-cover bg-zinc-100" />
                <div className="p-2.5 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-zinc-800 truncate">{b.name}</p>
                    <span className="text-[10px] uppercase text-zinc-400">{b.layout}</span>
                  </div>
                  {b.headline && <p className="text-[11px] text-zinc-500 line-clamp-2 whitespace-pre-wrap">{b.headline}</p>}
                  <div className="flex items-center justify-between pt-1">
                    <label className="flex items-center gap-1.5 text-[11px] text-zinc-600">
                      <Switch checked={b.active} onCheckedChange={() => toggleActive(b)} data-testid={`banner-toggle-${b.id}`} />
                      {b.active ? 'Active' : 'Hidden'}
                    </label>
                    <button onClick={() => remove(b)} className="text-red-400 hover:text-red-600" data-testid={`banner-delete-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AffiliateBannerAdmin;
